import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { getRequest, postRequest } from '../services/apiService'; // Only postRequest is needed for CUD
import AddMenuModal from '../accessPrivileges/AddMenuModal';
import MenuItem from '../accessPrivileges/MenuItem';
import ReusableDeleteDialog from '../common/ReusableDeleteDialog';

export default function AccessPrivileges() {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [showAddMainMenuModal, setShowAddMainMenuModal] = useState(false);
  const [showAddSubMenuModal, setShowAddSubMenuModal] = useState(false);
  const [currentParentId, setCurrentParentId] = useState(null);
  const [newMenuName, setNewMenuName] = useState('');
  const [newMenuUrl, setNewMenuUrl] = useState('');
  const [newMenuIcon, setNewMenuIcon] = useState('');
  const [newMenuSequence, setNewMenuSequence] = useState(''); // New state for sequence
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionsList, setPermissionsList] = useState([]);
  const [editingMenu, setEditingMenu] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState(null);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: "" });
  const [activeMenuId, setActiveMenuId] = useState(null); // State to track the currently open action menu

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setIsLoading(true);
        const permissionsData = await getRequest('access/privileges/');
        setPermissionsList(permissionsData);
        setIsLoading(false);
      } catch (error) {
        setError('Failed to fetch permissions');
        setIsLoading(false);
        console.error('Error fetching permissions:', error);
      }
    };
    fetchPermissions();
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsLoading(true);
        const rolesData = await getRequest('access/user-roles/');
        setRoles(rolesData);
        setIsLoading(false);
      } catch (error) {
        setError('Failed to fetch roles');
        setIsLoading(false);
        console.error('Error fetching roles:', error);
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchMenusWithPrivileges = async () => {
      if (!selectedRole) {
        setMenuItems([]);
        return;
      }
      try {
        setIsLoading(true);
        const menusData = await getRequest(`access/role-menu-mappings/?role_id=${selectedRole}`);
        const transformedMenus = menusData.map(item => transformMenuFormat(item));
        // Sort the main menu items by sequence
        const sortedMenus = transformedMenus.sort((a, b) => a.sequence - b.sequence);
        setMenuItems(sortedMenus);
        setIsLoading(false);
      } catch (error) {
        setError('Failed to fetch menus with privileges');
        setIsLoading(false);
        console.error('Error fetching menus with privileges:', error);
      }
    };
    fetchMenusWithPrivileges();
  }, [selectedRole]);

  const transformMenuFormat = (item) => {
    const permissions = {};
    if (item.privileges && item.privileges.length > 0) {
      item.privileges.forEach(priv => {
        permissions[priv.name.toLowerCase()] = priv.status;
      });
    }
    // Recursively sort sub-menus as well
    const sortedChildren = item.sub_menus ? item.sub_menus.map(subItem =>
      transformMenuFormat(subItem)
    ).sort((a, b) => a.sequence - b.sequence) : [];

    return {
      id: item.id,
      name: item.name,
      url: item.url,
      icon: item.icon,
      sequence: item.sequence, // Include sequence
      expanded: true,
      enabled: true,
      permissions: permissions,
      children: sortedChildren
    };
  };

  const toggleExpand = (id, items = menuItems) => {
    return items.map(item => {
      if (item.id === id) {
        return { ...item, expanded: !item.expanded };
      } else if (item.children && item.children.length > 0) {
        return { ...item, children: toggleExpand(id, item.children) };
      }
      return item;
    });
  };

  const toggleEnabled = (id, items = menuItems) => {
    return items.map(item => {
      if (item.id === id) {
        const newEnabledState = !item.enabled;
        return {
          ...item,
          enabled: newEnabledState,
          children: item.children.map(child => toggleEnabledRecursive(child, newEnabledState))
        };
      } else if (item.children && item.children.length > 0) {
        return { ...item, children: toggleEnabled(id, item.children) };
      }
      return item;
    });
  };

  const toggleEnabledRecursive = (item, enabledState) => {
    return {
      ...item,
      enabled: enabledState,
      children: item.children.map(child => toggleEnabledRecursive(child, enabledState))
    };
  };

  const updatePermission = (id, permKey, value, items = menuItems, updateChildren = true, updateParents = false, parentChain = []) => {
    return items.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          permissions: { ...item.permissions, [permKey]: value }
        };
        if (item.children && item.children.length > 0) {
          updatedItem.children = updatedItem.children.map(child =>
            updatePermissionRecursive(child, permKey, value)
          );
        }
        if (updateParents && parentChain.length > 0 && value) {
          parentChain.forEach(parent => {
            parent.permissions = { ...parent.permissions, [permKey]: true };
          });
        }
        return updatedItem;
      } else if (item.children && item.children.length > 0) {
        const newParentChain = updateParents ? [...parentChain, item] : parentChain;
        const updatedChildren = updatePermission(
          id,
          permKey,
          value,
          item.children,
          updateChildren,
          updateParents,
          newParentChain
        );
        return { ...item, children: updatedChildren };
      }
      return item;
    });
  };

  const updatePermissionRecursive = (item, permKey, value) => {
    return {
      ...item,
      permissions: { ...item.permissions, [permKey]: value },
      children: item.children.map(child => updatePermissionRecursive(child, permKey, value))
    };
  };

  const handleEditMenu = (menu) => {
    setEditingMenu(menu);
    setNewMenuName(menu.name);
    setNewMenuUrl(menu.url);
    setNewMenuIcon(menu.icon);
    setNewMenuSequence(menu.sequence); // Set sequence for editing
    setShowAddMainMenuModal(true);
    setActiveMenuId(null); // Close any open action menu when opening edit modal
  };

  const removeMenuById = (items, id) => {
    return items.filter(item => item.id !== id).map(item => {
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: removeMenuById(item.children, id)
        };
      }
      return item;
    });
  };

  const addMainMenu = async () => {
    if (!newMenuName.trim()) return;
    try {
      setIsLoading(true);
      let menuData = {
        name: newMenuName,
        url: newMenuUrl || "/",
        icon: newMenuIcon || "MessageSquare",
        sequence: newMenuSequence, // Include sequence
        status_db: 1 // Default to 1 for new main menu
      };

      if (editingMenu) {
        menuData = {
          ...menuData,
          id: editingMenu.id, // Include the ID for update
          status_db: 2 // Assuming 2 signifies an update
        };
      }

      const response = await postRequest('access/menus/', menuData); // Use postRequest for both add and edit

      if (editingMenu) {
        const updatedMenus = menuItems.map(item => {
          if (item.id === editingMenu.id) {
            return {
              ...item,
              name: response.name,
              url: response.url,
              icon: response.icon,
              sequence: response.sequence // Update sequence on edit
            };
          }
          return item;
        }).sort((a, b) => a.sequence - b.sequence); // Sort after update
        setMenuItems(updatedMenus);
      } else {
        const newMenu = transformMenuFormat({
          id: response.id,
          name: response.name,
          url: response.url,
          icon: response.icon,
          sequence: response.sequence, // Set sequence for new menu
          sub_menus: []
        });
        setMenuItems([...menuItems, newMenu].sort((a, b) => a.sequence - b.sequence)); // Sort by sequence
      }
      setIsLoading(false);
      setNewMenuName('');
      setNewMenuUrl('');
      setNewMenuIcon('');
      setNewMenuSequence(''); // Clear sequence after save
      setShowAddMainMenuModal(false);
      setEditingMenu(null);
      // window.location.reload();
    } catch (error) {
      setError(editingMenu ? 'Failed to update menu' : 'Failed to add menu');
      setIsLoading(false);
      console.error('Error:', error);
    }
  };

  const addSubMenu = async () => {
    if (!newMenuName.trim() || !currentParentId) return;
    try {
      setIsLoading(true);
      const subMenuData = {
        name: newMenuName,
        url: newMenuUrl || "/",
        icon: newMenuIcon || "icon",
        sequence: newMenuSequence, // Include sequence for submenu
        parent: currentParentId,
        status_db: 1
      };
      const response = await postRequest('access/menus/', subMenuData);
      const newSubMenu = transformMenuFormat({
        id: response.id,
        name: response.name,
        url: response.url,
        icon: response.icon,
        sequence: response.sequence, // Set sequence for new submenu
        sub_menus: []
      });
      const updatedMenuItems = addSubmenuToParent(menuItems, currentParentId, newSubMenu);
      setMenuItems(updatedMenuItems);
      setIsLoading(false);
      setNewMenuName('');
      setNewMenuUrl('');
      setNewMenuIcon('');
      setNewMenuSequence(''); // Clear sequence after save
      setShowAddSubMenuModal(false);
      // window.location.reload();
    } catch (error) {
      setError('Failed to add submenu');
      setIsLoading(false);
      console.error('Error adding submenu:', error);
    }
  };

  const addSubmenuToParent = (items, parentId, newSubMenu) => {
    return items.map(item => {
      if (item.id === parentId) {
        const updatedChildren = [...item.children, newSubMenu].sort((a, b) => a.sequence - b.sequence); // Sort children
        return {
          ...item,
          expanded: true,
          children: updatedChildren
        };
      } else if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: addSubmenuToParent(item.children, parentId, newSubMenu)
        };
      }
      return item;
    });
  };

  const savePermissions = async () => {
    if (!selectedRole) return;
    try {
      setIsLoading(true);
      const permissionsData = {
        role: { id: parseInt(selectedRole) },
        menu_access: transformMenuItemsForApi(menuItems)
      };
      await postRequest('access/role-menu-mappings/', permissionsData);
      setIsLoading(false);
    } catch (error) {
      setError('Failed to save permissions');
      setIsLoading(false);
      console.error('Error saving permissions:', error);
    }
  };

  const transformMenuItemsForApi = (items) => {
    return items.map(item => {
      const privileges = [];
      if (item.permissions) {
        for (const [key, value] of Object.entries(item.permissions)) {
          const permission = permissionsList.find(p => p.name.toLowerCase() === key);
          if (permission) {
            privileges.push({
              id: permission.id,
              name: permission.name,
              description: permission.description,
              status: value
            });
          }
        }
      }
      return {
        id: item.id,
        name: item.name,
        url: item.url,
        icon: item.icon,
        sequence: item.sequence, // Include sequence in API payload
        sub_menus: item.children ? transformMenuItemsForApi(item.children) : [],
        privileges: privileges
      };
    });
  };

  const handleDeleteMenu = (menuId) => {
    setMenuToDelete(menuId);
    setShowDeleteModal(true);
    setActiveMenuId(null); // Close any open action menu when opening delete modal
  };

  const handleDeleteConfirm = async (e) => {
    e.preventDefault();
    if (deleteFormData.confirmation !== "DELETE") {
      setError('Please type "DELETE" to confirm deletion');
      return;
    }
    try {
      setIsLoading(true);
      const deletePayload = {
        id: menuToDelete,
        status_db: 3 // 3 signifies deletion
      };
      await postRequest(`access/menus/`, deletePayload);
      const updatedMenus = removeMenuById(menuItems, menuToDelete);
      setMenuItems(updatedMenus);
      setShowDeleteModal(false);
      setMenuToDelete(null);
      setDeleteFormData({ confirmation: "" });
      // window.location.reload();
    } catch (error) {
      setError('Failed to delete menu');
      console.error('Error deleting menu:', error);
    } finally {
      setIsLoading(false);
    }
  };

  function getGridColsClass(length) {
    const maxCols = 12;
    const colCount = Math.min(length, maxCols);
    return `grid-cols-${colCount}`;
  }

  return (
    <div className="container mx-auto p-2 sm:p-4 bg-white min-h-screen">
      {/* Header Section - Responsive */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 sm:mb-6 space-y-4 lg:space-y-0">
        <div className="flex items-center">
          <h1 className="text-xl sm:text-2xl font-bold text-blue-700">Access Privileges</h1>
          <button
            onClick={() => {
              setShowAddMainMenuModal(true);
              setEditingMenu(null); // Ensure editingMenu is null for add operation
              setNewMenuName(''); // Clear fields
              setNewMenuUrl('');
              setNewMenuIcon('');
              setNewMenuSequence(''); // Clear sequence field
              setActiveMenuId(null); // Close any open action menu when opening add main menu modal
            }}
            className="ml-2 sm:ml-4 p-1 text-blue-500 hover:bg-blue-50 rounded-full transition-colors"
          >
            <Plus size={20} className="sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Controls Section - Responsive */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full lg:w-auto">
          <div className="flex flex-col sm:flex-row sm:items-center w-full sm:w-auto">
            <span className="mb-1 sm:mb-0 sm:mr-2 text-sm font-medium">Select Role</span>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full sm:w-auto border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            >
              <option value="">Select a role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>
          <button
            className="w-full sm:w-auto bg-blue-500 text-white px-4 sm:px-6 py-2 rounded-md disabled:bg-blue-300 text-sm font-medium transition-colors hover:bg-blue-600"
            onClick={savePermissions}
            disabled={!selectedRole || isLoading}
          >
            {isLoading ? 'Processing...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Main Content */}
      {selectedRole ? (
        isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        ) : (
          <div className={`border rounded-md shadow-sm ${activeMenuId ? 'overflow-visible' : 'overflow-hidden'}`}>
            {/* Desktop Table Header */}
            <div className="hidden lg:flex border-b border-gray-200 bg-gray-50 font-medium text-sm">
              <div className="w-60 xl:w-72 p-3 border-r border-gray-200">Menu</div>
              <div className={`flex-1 grid text-center gap-2 ${getGridColsClass(permissionsList.length)} border-r border-gray-200`}>
                {permissionsList.map((permission) => (
                  <div key={permission.id} className="p-2 xl:p-3 border-r border-gray-100 last:border-r-0">
                    <span className="text-xs xl:text-sm">{permission.name}</span>
                  </div>
                ))}
              </div>
              <div className="w-16 xl:w-20 p-3 text-center border-r border-gray-200">Active</div>
              <div className="w-16 xl:w-24 p-3 text-center">Actions</div>
            </div>

            {/* Mobile/Tablet Header */}
            <div className="lg:hidden bg-gray-50 p-3 border-b border-gray-200">
              <div className="text-sm font-medium text-gray-700">
                Menu Items and Permissions
              </div>
            </div>

            {/* Menu Items */}
            <div className="bg-white">
              {menuItems.length > 0 ? (
                menuItems.map((item) => (
                  <MenuItem
                    key={item.id}
                    item={item}
                    toggleExpand={(id) => setMenuItems(toggleExpand(id))}
                    updatePermission={(id, permKey, value, updateChildren, updateParents) =>
                      setMenuItems(updatePermission(id, permKey, value, menuItems, updateChildren, updateParents))
                    }
                    toggleEnabled={(id) => setMenuItems(toggleEnabled(id))}
                    setCurrentParentId={setCurrentParentId}
                    setShowAddSubMenuModal={setShowAddSubMenuModal}
                    permissionsList={permissionsList}
                    onEdit={handleEditMenu}
                    onDelete={handleDeleteMenu}
                    activeMenuId={activeMenuId} // Pass activeMenuId
                    setActiveMenuId={setActiveMenuId} // Pass setActiveMenuId
                  />
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <div className="text-lg mb-2">No menu items available</div>
                  <div className="text-sm">Add a menu item to get started</div>
                </div>
              )}
            </div>
          </div>
        )
      ) : (
        <div className="text-center py-12 text-gray-500">
          <div className="text-lg mb-2">Please select a role</div>
          <div className="text-sm">Select a role from the dropdown above to manage access privileges</div>
        </div>
      )}

      {/* Modals */}
      <AddMenuModal
        show={showAddMainMenuModal}
        onClose={() => {
          setShowAddMainMenuModal(false);
          setNewMenuName('');
          setNewMenuUrl('');
          setNewMenuIcon('');
          setNewMenuSequence(''); // Clear sequence field on close
          setEditingMenu(null);
        }}
        title={editingMenu ? "Edit Menu" : "Add Main Menu"}
        onSave={addMainMenu}
        isSubmenu={false}
        newMenuName={newMenuName}
        setNewMenuName={setNewMenuName}
        newMenuUrl={newMenuUrl}
        setNewMenuUrl={setNewMenuUrl}
        newMenuIcon={newMenuIcon}
        setNewMenuIcon={setNewMenuIcon}
        newMenuSequence={newMenuSequence} // Pass sequence
        setNewMenuSequence={setNewMenuSequence} // Pass sequence setter
      />
      <AddMenuModal
        show={showAddSubMenuModal}
        onClose={() => {
          setShowAddSubMenuModal(false);
          setNewMenuName('');
          setNewMenuUrl('');
          setNewMenuIcon('');
          setNewMenuSequence(''); // Clear sequence field on close
        }}
        title="Add Sub Menu"
        onSave={addSubMenu}
        isSubmenu={true}
        newMenuName={newMenuName}
        setNewMenuName={setNewMenuName}
        newMenuUrl={newMenuUrl}
        setNewMenuUrl={setNewMenuUrl}
        newMenuIcon={newMenuIcon}
        setNewMenuIcon={setNewMenuIcon}
        newMenuSequence={newMenuSequence} // Pass sequence
        setNewMenuSequence={setNewMenuSequence} // Pass sequence setter
      />
      <ReusableDeleteDialog
        visible={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setDeleteFormData({ confirmation: "" });
        }}
        onDelete={handleDeleteConfirm}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={isLoading}
        title="Delete Menu"
        description="Are you sure you want to delete this menu? This action cannot be undone."
        deleteLabel="Delete Menu"
      />
    </div>
  );
}